import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { CreditCard, Sparkles, ExternalLink } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import {
  createCheckoutSession,
  createCustomerPortalSession,
  getMyBillingAccount,
} from "@/lib/billing.functions";
import { PLANS, PLAN_ORDER, type PlanKey, type BillingInterval } from "@/lib/billing-plans";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/settings/billing")({
  component: BillingPage,
});

function BillingPage() {
  const { user } = useAuth();
  const fetchAccount = useServerFn(getMyBillingAccount);
  const checkout = useServerFn(createCheckoutSession);
  const portal = useServerFn(createCustomerPortalSession);

  const [account, setAccount] = useState<Awaited<ReturnType<typeof fetchAccount>>["account"]>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState<PlanKey>("pro");
  const [interval, setInterval] = useState<BillingInterval>("monthly");

  useEffect(() => {
    if (!user) return;
    fetchAccount().then((r) => {
      setAccount(r.account);
      if (r.account?.plan_key) setPlan(r.account.plan_key);
      if (r.account?.billing_interval) setInterval(r.account.billing_interval as BillingInterval);
      setLoading(false);
    });
  }, [user, fetchAccount]);

  const trialDaysLeft = account?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(account.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const hasSubscription = !!account?.stripe_subscription_id;

  const onSubscribe = async () => {
    setBusy(true);
    try {
      const res = await checkout({ data: { plan, interval, origin: window.location.origin } });
      if (res?.url) window.location.href = res.url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start checkout");
      setBusy(false);
    }
  };

  const onPortal = async () => {
    setBusy(true);
    try {
      const res = await portal({ data: { origin: window.location.origin } });
      if (res?.url) window.location.href = res.url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open billing portal");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium">Current plan</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {loading
                ? "Loading…"
                : account
                  ? `${account.workspace_limit} workspaces · ${account.team_member_limit} team seats`
                  : "No account found"}
            </p>
          </div>
          {account && (
            <Badge variant={account.plan === "trial" ? "secondary" : "default"} className="capitalize">
              {account.plan.replace("_", " ")}
            </Badge>
          )}
        </div>

        {account?.plan_status === "trialing" && trialDaysLeft !== null && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
            <Sparkles className="h-4 w-4 text-primary" />
            <span>
              {trialDaysLeft > 0
                ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left in your trial`
                : "Trial ended, your card will be charged soon"}
            </span>
          </div>
        )}

        {account?.current_period_end && (
          <p className="mt-3 text-xs text-muted-foreground">
            {account.cancel_at_period_end ? "Cancels" : "Renews"} on{" "}
            {new Date(account.current_period_end).toLocaleDateString()}
          </p>
        )}
      </div>

      {hasSubscription ? (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-base font-medium">Manage subscription</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Change plan, update payment method, download invoices, or cancel.
          </p>
          <Button onClick={onPortal} disabled={busy} className="mt-4 gap-2">
            <CreditCard className="h-4 w-4" />
            Open billing portal
            <ExternalLink className="h-3.5 w-3.5 opacity-70" />
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-base font-medium">Choose a plan</h2>
          <p className="mt-1 text-sm text-muted-foreground">14-day free trial. No charge today.</p>

          <div className="mt-4 grid grid-cols-2 rounded-lg border border-border bg-muted/30 p-1 text-sm">
            <button
              type="button"
              onClick={() => setInterval("monthly")}
              className={cn("rounded-md py-1.5", interval === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground")}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval("annual")}
              className={cn("rounded-md py-1.5", interval === "annual" ? "bg-background shadow-sm" : "text-muted-foreground")}
            >
              Annual <span className="ml-1 text-xs text-emerald-500">save ~30%</span>
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {PLAN_ORDER.map((key) => {
              const def = PLANS[key];
              const price = def.display[interval];
              const selected = plan === key;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setPlan(key)}
                  className={cn(
                    "w-full rounded-lg border p-3 text-left transition",
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{def.name}</div>
                    <div className="text-sm font-semibold">
                      ${price.amount}
                      <span className="text-xs font-normal text-muted-foreground">{price.suffix}</span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{def.tagline}</div>
                </button>
              );
            })}
          </div>

          <Button onClick={onSubscribe} disabled={busy} className="mt-4 w-full gap-2">
            <CreditCard className="h-4 w-4" />
            {busy ? "Starting checkout…" : "Start 14-day trial"}
          </Button>
        </div>
      )}

      {account?.stripe_customer_id && (
        <p className="font-mono text-xs text-muted-foreground">
          Customer: {account.stripe_customer_id}
        </p>
      )}
    </div>
  );
}
